/**
 * OperationHistoryTimeline Component
 *
 * Assembles date buckets, dividers, and events into a continuous
 * vertical timeline with a rail (connector line).
 */

import type { JSX } from 'react';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent,
} from '../OperationHistory.types';
import type { DateBucket } from '../hooks/useOperationHistoryDateBuckets';

import { OperationHistoryDateDivider } from './OperationHistoryDateDivider';
import { OperationHistoryEventRow } from './OperationHistoryEvent';
import { OperationHistoryEventGroup } from './OperationHistoryEventGroup';
import styles from './OperationHistoryTimeline.module.css';

interface OperationHistoryTimelineProps<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  dateBuckets: DateBucket<TEvent>[];
  config: OperationHistoryConfig<TEvent>;
}

export function OperationHistoryTimeline<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
>({ dateBuckets, config }: OperationHistoryTimelineProps<TEvent>): JSX.Element {
  return (
    <div className={styles.timeline}>
      {dateBuckets.map((bucket, bucketIndex) => (
        <div
          key={bucket.groups[0]?.id ?? `${bucket.label}-${bucketIndex}`}
          className={styles.bucket}
        >
          <OperationHistoryDateDivider label={bucket.label} />
          <div className={styles.events}>
            {bucket.groups.map((group, groupIndex) =>
              group.isGrouped ? (
                <OperationHistoryEventGroup
                  key={group.id}
                  group={group}
                  config={config}
                  defaultExpanded={bucketIndex === 0 && groupIndex === 0}
                />
              ) : (
                group.events[0] && (
                  <OperationHistoryEventRow
                    key={group.id}
                    event={group.events[0]}
                    config={config}
                  />
                )
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
